import { useEffect, useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateNotificationConfig } from "@/hooks/use-api";
import type { NotificationCategory, NotificationConfig } from "@/types/api";
import { REDACTED_SENTINEL } from "@/types/api";
import { CategoryMultiSelect } from "./category-multi-select";

interface EditNotificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: NotificationConfig | null;
}

function configValueFromConfig(c: NotificationConfig): string {
  switch (c.channel) {
    case "email": {
      const recipients = c.config.recipients;
      if (Array.isArray(recipients)) return recipients.join(", ");
      return "";
    }
    case "slack": {
      const url = c.config.webhook_url;
      return typeof url === "string" ? url : "";
    }
    case "webhook": {
      const url = c.config.url;
      return typeof url === "string" ? url : "";
    }
    default:
      return "";
  }
}

export function EditNotificationDialog({
  open,
  onOpenChange,
  config,
}: EditNotificationDialogProps) {
  const [minSeverity, setMinSeverity] = useState("medium");
  const [configValue, setConfigValue] = useState("");
  const [categories, setCategories] = useState<NotificationCategory[]>([]);
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useUpdateNotificationConfig();

  // Prefill from incoming config whenever the dialog opens with a new target
  useEffect(() => {
    if (!config) return;
    setMinSeverity(config.min_severity);
    setConfigValue(configValueFromConfig(config));
    setCategories(config.category_filter ?? []);
    setError(null);
  }, [config]);

  function handleClose() {
    setError(null);
    onOpenChange(false);
  }

  async function handleSubmit() {
    if (!config) return;
    if (!configValue.trim()) {
      setError("Configuration value is required");
      return;
    }

    // Build the config payload. Preserve any existing redacted secrets
    // by including the sentinel verbatim — the backend recognizes it and
    // keeps the stored value.
    const nextConfig: Record<string, unknown> = {};
    switch (config.channel) {
      case "email":
        nextConfig.recipients = configValue
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        break;
      case "slack":
        nextConfig.webhook_url = configValue.trim();
        break;
      case "webhook":
        nextConfig.url = configValue.trim();
        // Forward redacted signing_secret sentinel so backend preserves it
        if (config.config.signing_secret === REDACTED_SENTINEL) {
          nextConfig.signing_secret = REDACTED_SENTINEL;
        }
        break;
    }

    setError(null);
    try {
      await updateMutation.mutateAsync({
        configId: config.id,
        config: nextConfig,
        min_severity: minSeverity,
        category_filter: categories,
      });
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update notification",
      );
    }
  }

  if (!open || !config) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close edit notification dialog"
        className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Edit Notification Channel"
          className="pointer-events-auto relative w-full max-w-md rounded-lg border bg-background shadow-lg"
        >
          <div className="flex items-center justify-between border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Edit Notification Channel</h2>
            <button
              type="button"
              className="rounded-sm p-1 opacity-70 hover:opacity-100 transition-opacity"
              onClick={handleClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4 p-6">
            <div>
              <Label htmlFor="edit-channel">Channel Type</Label>
              <Input
                id="edit-channel"
                value={config.channel}
                readOnly
                disabled
                aria-readonly="true"
                className="mt-1.5 capitalize"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Channel type is immutable. Delete and recreate to change.
              </p>
            </div>

            <div>
              <Label htmlFor="edit-config-value">
                {config.channel === "email"
                  ? "Recipients (comma separated)"
                  : config.channel === "slack"
                    ? "Slack Webhook URL"
                    : "Webhook URL"}
              </Label>
              <Input
                id="edit-config-value"
                value={configValue}
                onChange={(e) => setConfigValue(e.target.value)}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="edit-min-severity">Minimum Severity</Label>
              <select
                id="edit-min-severity"
                value={minSeverity}
                onChange={(e) => setMinSeverity(e.target.value)}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <CategoryMultiSelect
              id="edit-categories"
              value={categories}
              onChange={setCategories}
            />

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSubmit}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                <Save className="mr-1.5 h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
